<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();

const tabs = [
  {
    name: 'appointments_agenda',
    labelKey: 'APPOINTMENTS.AGENDA.NAV_LABEL',
    icon: 'i-lucide-calendar-days',
  },
  {
    name: 'appointments_clients',
    labelKey: 'APPOINTMENTS.CLIENTS.NAV_LABEL',
    icon: 'i-lucide-users',
  },
  {
    name: 'appointments_professionals',
    labelKey: 'APPOINTMENTS.PROFESSIONALS.NAV_LABEL',
    icon: 'i-lucide-user-round',
  },
  {
    name: 'appointments_services',
    labelKey: 'APPOINTMENTS.SERVICES.NAV_LABEL',
    icon: 'i-lucide-clipboard-list',
  },
  {
    name: 'appointments_partners',
    labelKey: 'APPOINTMENTS.PARTNERS.NAV_LABEL',
    icon: 'i-lucide-handshake',
  },
  {
    name: 'appointments_reports',
    labelKey: 'APPOINTMENTS.REPORTS.NAV_LABEL',
    icon: 'i-lucide-bar-chart-3',
  },
];

// Header dinâmico baseado na rota ativa
const HEADER_BY_ROUTE = {
  appointments_agenda: {
    icon: 'i-lucide-calendar-days',
    iconBg: 'bg-n-brand/10',
    iconColor: 'text-n-brand',
    titleKey: 'APPOINTMENTS.AGENDA.TITLE',
    descriptionKey: 'APPOINTMENTS.AGENDA.SUBTITLE',
    descriptionFallback: 'Calendário com seus atendimentos agendados.',
  },
  appointments_clients: {
    icon: 'i-lucide-users',
    iconBg: 'bg-n-teal-3',
    iconColor: 'text-n-teal-11',
    titleKey: 'APPOINTMENTS.CLIENTS.TITLE',
    descriptionKey: 'APPOINTMENTS.CLIENTS.SUBTITLE',
    descriptionFallback: 'Pessoas atendidas pelos seus profissionais.',
  },
  appointments_professionals: {
    icon: 'i-lucide-user-cog',
    iconBg: 'bg-n-blue-3',
    iconColor: 'text-n-blue-11',
    titleKey: 'APPOINTMENTS.PROFESSIONALS.TITLE',
    descriptionKey: 'APPOINTMENTS.PROFESSIONALS.SUBTITLE',
    descriptionFallback: 'Profissionais que prestam atendimentos no seu time.',
  },
  appointments_services: {
    icon: 'i-lucide-briefcase',
    iconBg: 'bg-n-brand/10',
    iconColor: 'text-n-brand',
    titleKey: 'APPOINTMENTS.SERVICES.TITLE',
    descriptionKey: 'APPOINTMENTS.SERVICES.SUBTITLE',
    descriptionFallback: 'Catálogo de serviços oferecidos pelos profissionais.',
  },
  appointments_partners: {
    icon: 'i-lucide-handshake',
    iconBg: 'bg-n-iris-3',
    iconColor: 'text-n-iris-11',
    titleKey: 'APPOINTMENTS.PARTNERS.TITLE',
    descriptionKey: 'APPOINTMENTS.PARTNERS.SUBTITLE',
    descriptionFallback: 'Convênios, seguros e planos cadastrados.',
  },
  appointments_reports: {
    icon: 'i-lucide-bar-chart-3',
    iconBg: 'bg-n-amber-3',
    iconColor: 'text-n-amber-11',
    titleKey: 'APPOINTMENTS.REPORTS.TITLE',
    descriptionKey: 'APPOINTMENTS.REPORTS.SUBTITLE',
    descriptionFallback: 'Métricas e desempenho dos atendimentos.',
  },
};

const currentHeader = computed(
  () => HEADER_BY_ROUTE[route.name] || HEADER_BY_ROUTE.appointments_agenda
);
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0 bg-n-background">
    <!-- Header global Premium -->
    <div
      class="px-6 lg:px-8 py-6 relative overflow-hidden bg-gradient-to-b from-n-alpha-1/50 to-transparent"
    >
      <div
        class="absolute inset-0 bg-gradient-to-r from-transparent via-n-brand/5 to-transparent pointer-events-none opacity-50"
      />

      <div class="flex items-center gap-4 min-w-0 relative z-10">
        <div
          class="size-12 rounded-[1.25rem] flex items-center justify-center flex-shrink-0 ring-1 ring-black/5 shadow-sm"
          :class="currentHeader.iconBg"
        >
          <span
            class="size-6 drop-shadow-sm"
            :class="[currentHeader.icon, currentHeader.iconColor]"
            aria-hidden="true"
          />
        </div>
        <div class="min-w-0">
          <h1
            class="text-[1.35rem] font-bold text-n-slate-12 tracking-tight truncate leading-tight"
          >
            {{ t(currentHeader.titleKey) }}
          </h1>
          <p
            class="text-sm font-medium text-n-slate-10 mt-0.5 truncate tracking-wide"
          >
            {{
              t(currentHeader.descriptionKey, currentHeader.descriptionFallback)
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Tabs nav modernas com under-indicator dinâmico -->
    <nav
      class="flex gap-2 px-6 lg:px-8 overflow-x-auto whitespace-nowrap custom-scrollbar border-b border-n-weak/80 relative"
      :aria-label="t('APPOINTMENTS.AGENDA.TITLE')"
    >
      <router-link
        v-for="tab in tabs"
        :key="tab.name"
        :to="{ name: tab.name }"
        class="group relative flex-shrink-0 inline-flex items-center gap-2 px-4 py-3.5 text-[13px] font-bold uppercase tracking-wider text-n-slate-10 hover:text-n-slate-12 transition-colors focus:outline-none focus:text-n-slate-12"
        active-class="!text-n-brand"
        :data-testid="`tab-${tab.name}`"
      >
        <span
          :class="tab.icon"
          class="size-4 opacity-70 group-hover:opacity-100 transition-opacity"
          aria-hidden="true"
        />
        {{ t(tab.labelKey) }}

        <!-- Active indicator animado -->
        <div
          class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full bg-n-brand scale-x-0 opacity-0 transition-all duration-300 origin-center group-[.router-link-active]:scale-x-100 group-[.router-link-active]:opacity-100"
        />
      </router-link>
    </nav>

    <main class="flex-1 overflow-auto w-full relative">
      <router-view v-slot="{ Component }">
        <transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          mode="out-in"
        >
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
  </div>
</template>
